/**
 * Gameplay object replication: dropped items, XP orbs, arrows, chest and
 * furnace contents, TNT lit by a client, and the damage the host deals to a
 * remote player. Blocks already travel through the session's edit log, mobs
 * through K_ENTS; everything else in the world lives here.
 *
 * Authority. The host owns these objects exactly as it owns blocks: clients
 * mirror what it sends and never assert state they did not observe. Clients
 * send back only *requests* (I dropped this, I lit this, I changed this chest,
 * this drop is gone) and the host checks reach and rate before acting.
 *
 * Transport. NetSession owns the transport and only knows its own kinds, so
 * GameObjectNet wraps `transport.onMessage`: the session still sees every kind
 * it knows, this sees the new ones. State is accepted from the current host
 * only (a client), or from a member (the host). The unreliable state messages
 * are idempotent - deltas plus a keyframe every 20 ticks - so a dropped packet
 * heals on the next keyframe instead of drifting.
 *
 * Bandwidth: per peer, objects are filtered to what is near that player and
 * sent only when their signature changed (block entities every 10 ticks, a
 * keyframe every 40).
 */

import type { ItemEntity, ItemEntityManager } from '../entities/itemEntities';
import type { Arrow } from '../entities/arrow';
import type { XpOrbManager } from '../entities/xp';
import { liveTnt, placeXp, removeXp, xpOrbs } from './handles';
import {
  AF_CRIT, AF_FLAME, AF_PLAYER, AF_STUCK, BE_CHEST, BE_FURNACE, DK_ITEM, DK_ORB,
  K_ARROWS, K_BE, K_BEREQ, K_DMG, K_DROP, K_DROPS, K_TAKE, K_TNT,
  decodeArrows, decodeBe, decodeBeReq, decodeDamage, decodeDrop, decodeDrops, decodeTake, decodeTntReq,
  encodeArrows, encodeBe, encodeBeReq, encodeDamage, encodeDrop, encodeDrops, encodeTake, encodeTntReq, stackExtras, stackWith,
  type ArrowState, type ArrowsMsg, type BeEntry, type BeMsg, type DropState, type DropsMsg,
} from './codec';
import type { NetSession } from './session';
import type { Transport } from './transport';
import { RateLimiter, withinReach } from './validate';
import { FUSE_TICKS, type Charge } from '../world/tnt';
import { TNT_ID } from '../blocks';

/** How far from a player objects are worth sending (horizontal blocks). */
const VIEW = 96;
/** Block entities travel rarely: they only change when a player opens one. */
const BE_TICKS = 10;
const BE_KEYFRAME = 40;
const BE_RANGE = 32;
/** How often a client looks for a block entity it changed locally. */
const BE_POLL = 5;
/** Pickup boxes (item entities inflate the player by (1, 0.5, 1), like vanilla). */
const ITEM_PICK = 1.6;
const ORB_PICK = 0.8;
/** How far from its own feet a client may claim to have dropped something. */
const DROP_REACH = 8;
/** Keyframe period for drops and arrows. */
const KEYFRAME = 20;
/** A furnace's burn/cook counters are the client's own sim: only these nine
 *  slot numbers are a player action and may be pushed to the host. */
const FURNACE_SLOTS = 9;
/** A mirrored item this far from the host's position is left to the next keyframe. */
const SNAP = 4;

/** A hit the host landed on a player (see K_DMG). */
export interface NetHit {
  amount: number;
  kind: string;
  x: number; y: number; z: number;
  attacker: number;
  kbx: number; kbz: number;
  fire: boolean;
}

/** A drop the host holds, as the wire sees it. */
export interface NetDrop {
  kind: number;
  id: number;
  item: number;
  count: number;
  dmg: number;
  x: number; y: number; z: number;
  /** Enchantments / name (codec stackExtras). */
  ex?: number[];
}

export interface ObjectNetDeps {
  /** The live session, or null outside multiplayer. */
  session(): NetSession | null;
  /** true while this peer owns the world. */
  isHost(): boolean;
  /** Block id at (x, y, z), or -1 when the chunk is not loaded. */
  getBlock(x: number, y: number, z: number): number;
  /** The dropped-item manager this tab renders and ticks. */
  items(): ItemEntityManager | null;
  /** XP orbs, once entities/xp.ts has ticked at least once. */
  xp(): XpOrbManager | null;
  /** Arrows this tab owns: real ones while hosting, mirrored ones on a client. */
  arrows(): readonly Arrow[];
  /** Host: put a drop a client asked for into the world under `netId`, and
   *  return its local id so the host can pin that id (null if it did not fit). */
  spawnDrop(d: NetDrop, netId: number, origin: number, vx: number, vy: number, vz: number): number | null;
  /** Host: take a drop out of the world by net id. */
  removeDrop(kind: number, netId: number): boolean;
  /** Client: mirror an arrow, returns its local id. */
  spawnArrow(a: ArrowState): number | null;
  /** Client: drop a mirrored arrow by its local id. */
  removeArrow(localId: number): void;
  /** Every block entity of `kind` within `r` blocks of (x, y, z). */
  beNear(kind: number, x: number, y: number, z: number, r: number): Array<{ x: number; y: number; z: number; flat: number[] }>;
  /** Write flat contents into a block entity ("x,y,z" key). */
  beWrite(kind: number, x: number, y: number, z: number, flat: number[]): void;
  /** Forget a block entity's contents (the block was broken). */
  beDrop(kind: number, x: number, y: number, z: number): void;
  /** Host: light the charge a client asked for. */
  primeTnt(x: number, y: number, z: number): void;
  /** Client: the host says we were hurt. */
  localHit(hit: NetHit): void;
  /** Client: a drop a remote player picked up is ours (goes into our inventory). */
  giveItem(item: number, count: number, dmg: number, ex?: number[]): void;
  /** Local player feet position. */
  me(): { x: number; y: number; z: number };
  /** Every player this peer knows about, the local one included. */
  players(): Array<{ id: string; x: number; y: number; z: number }>;
}

interface PeerSend {
  drops: Map<number, number>;
  dropKey: number;
  arrows: Map<number, number>;
  arrowKey: number;
  be: Map<string, number>;
  beKey: number;
  beTick: number;
  /** Request budgets: drops + pickups, block-entity writes, TNT. */
  rl: { drops: RateLimiter; bereq: RateLimiter; tnt: RateLimiter };
}

export class GameObjectNet {
  private readonly peers = new Map<string, PeerSend>();
  private wrapped: Transport | null = null;
  private previous: ((from: string, ch: 'rel' | 'unrel', data: Uint8Array) => void) | null = null;
  private nextNet = 1;
  private tickN = 0;
  private replica = false;
  private primedHooked = false;

  /** Host: local item id <-> net id (and the client request it came from). */
  private readonly netOf = new Map<number, number>();
  private readonly originOf = new Map<number, number>();
  /** Client: net id -> local item id. */
  private readonly localOf = new Map<number, number>();
  /** Client: every item entity that is a mirror of the host (never a local drop). */
  private readonly mirrors = new Set<ItemEntity>();
  /** Client: request id -> the local drop waiting to be confirmed. */
  private readonly pending = new Map<number, number>();
  private readonly pendingByLocal = new Map<number, number>();
  private reqSeq = 1;
  /** Client: XP orbs, net id -> local orb view id. */
  private readonly orbLocalOf = new Map<number, number>();
  private readonly orbNetOf = new Map<number, number>();
  /** Client: arrows. */
  private readonly arrowState = new Map<number, ArrowState>();
  private readonly arrowLocalOf = new Map<number, number>();
  private readonly arrowNetOf = new Map<number, number>();
  /** Client: the last contents the host sent, per block entity. */
  private readonly beSeen = new Map<string, number[]>();
  /** Client: what our own save held when we joined (stale copies the host may
   *  contradict: never pushed unless the player changes them). null = not taken yet. */
  private beBase: Map<string, number[]> | null = null;
  private readonly primedCells = new Map<string, number>();

  readonly stats = { drops: 0, arrows: 0, be: 0, taken: 0, denied: 0, dmg: 0 };

  constructor(private readonly d: ObjectNetDeps) {
    liveTnt((x, y, z) => this.onPrimed(x, y, z));
  }

  // ------------------------------------------------------------- plumbing

  /** Start (or stop) riding the session's transport. */
  attach(session: NetSession | null): void {
    this.detach();
    if (!session) return;
    const t = session.transport;
    const prev = t.onMessage;
    if (!prev) return;
    this.wrapped = t;
    this.previous = prev;
    t.onMessage = (from, ch, data) => {
      prev(from, ch, data);   // the session always sees the traffic
      if (this.d.session()) this.onMessage(from, ch, data);
    };
  }

  detach(): void {
    if (this.wrapped && this.previous) this.wrapped.onMessage = this.previous;
    this.wrapped = null;
    this.previous = null;
    this.reset();
  }

  /** Client <-> host switch: forget everything the other role owned. */
  setReplica(on: boolean): void {
    if (on === this.replica) return;
    this.replica = on;
    this.reset();
  }

  /**
   * Forget every id map and take the mirrors out of the world: they belonged
   * to the role we are leaving (a client that just became the host still shows
   * the old host's items until its own drop ids take over).
   */
  private reset(): void {
    this.peers.clear();
    this.netOf.clear();
    this.originOf.clear();
    this.localOf.clear();
    this.pending.clear();
    this.pendingByLocal.clear();
    this.arrowState.clear();
    this.arrowLocalOf.clear();
    this.arrowNetOf.clear();
    this.beSeen.clear();
    this.beBase = null;
    this.primedCells.clear();
    const items = this.d.items();
    for (const it of this.mirrors) {
      if (items?.items.includes(it)) it.stack.count = 0;
    }
    this.mirrors.clear();
    const xp = this.d.xp();
    for (const local of this.orbLocalOf.keys()) if (xp) removeXp(xp, local);
    this.orbLocalOf.clear();
    this.orbNetOf.clear();
    for (const local of this.arrowLocalOf.keys()) this.d.removeArrow(local);
  }

  /** A client lit TNT: the host has to prime a charge there too. */
  private onPrimed(x: number, y: number, z: number): void {
    if (this.primedHooked) return;
    this.primedHooked = true;
    const s = this.d.session();
    if (!s || s.isHost || s.status === 'closed') return;
    const cell = `${x},${y},${z}`;
    if (this.clock() - (this.primedCells.get(cell) ?? -1e9) < 1000) return;
    this.primedCells.set(cell, this.clock());
    this.sendRel(encodeTntReq(x, y, z));
  }

  // ----------------------------------------------------------------- tick

  /** 20 Hz, driven by GameBridge.tick() (the session's own timer). */
  tick(): void {
    const s = this.d.session();
    if (!s || s.status === 'closed' || s.status === 'syncing') return;
    this.tickN++;
    if (s.isHost) {
      this.hostDrops();
      this.hostArrows();
      this.hostBlockEntities();
      this.hostPickups();
    } else {
      this.clientDrops();
      this.clientArrows();
      this.clientBlockEntities();
    }
  }

  /** The host applied a client's block edit: TNT going to air is an ignition. */
  remoteBlockEdit(prev: number, id: number, x: number, y: number, z: number): void {
    if (prev !== TNT_ID || id !== 0) return;
    const s = this.d.session();
    if (!s || !s.isHost) return;
    this.prime(x, y, z);
  }

  // ----------------------------------------------------------------- host

  private hostDrops(): void {
    const items = this.d.items();
    const xp = this.d.xp();
    if (!items && !xp) return;
    const states: DropState[] = [];
    if (items) {
      for (const it of items.items) {
        if (it.collect > 0) continue;   // in the pickup animation already
        const net = this.netIdOf(it);
        states.push({
          id: net, kind: DK_ITEM, item: it.stack.id, count: Math.min(255, it.stack.count), dmg: it.stack.dmg,
          x: it.x, y: it.y, z: it.z, age: it.age & 0xffff, origin: this.originOf.get(net) ?? 0, ex: stackExtras(it.stack),
        });
      }
    }
    if (xp) {
      for (const o of xpOrbs(xp)) {
        states.push({
          id: o.id, kind: DK_ORB, item: o.value, count: Math.min(255, o.count), dmg: 0,
          x: o.x, y: o.y, z: o.z, age: o.age & 0xffff, origin: 0,
        });
      }
    }
    const s = this.d.session()!;
    for (const [id, peer] of s.peers) {
      const st = peer.accepted;
      if (!st || !s.members.has(id)) continue;
      const ps = this.peerSend(id);
      const keyframe = this.tickN - ps.dropKey >= KEYFRAME;
      if (keyframe) ps.dropKey = this.tickN;
      const drops: DropState[] = [];
      const seen = new Set<number>();
      for (const d of states) {
        if (Math.abs(d.x - st.x) > VIEW || Math.abs(d.z - st.z) > VIEW) continue;
        seen.add(d.id);
        const sig = dropSig(d);
        if (!keyframe && ps.drops.get(d.id) === sig) continue;
        ps.drops.set(d.id, sig);
        drops.push(d);
      }
      const removed: number[] = [];
      for (const net of [...ps.drops.keys()]) {
        if (seen.has(net)) continue;
        ps.drops.delete(net);
        removed.push(net);
      }
      if (!keyframe && !drops.length && !removed.length) continue;
      const msg: DropsMsg = { tick: this.tickN, keyframe, drops, removed };
      this.sendTo(id, 'unrel', encodeDrops(msg));
      this.stats.drops++;
    }
  }

  private hostArrows(): void {
    const s = this.d.session()!;
    const list: ArrowState[] = [];
    for (const a of this.d.arrows()) {
      if (a.removed) continue;
      list.push({
        id: a.id, x: a.x, y: a.y, z: a.z, vx: a.vx, vy: a.vy, vz: a.vz,
        flags: (a.stuck ? AF_STUCK : 0) | (a.fromPlayer ? AF_PLAYER : 0) | (a.crit ? AF_CRIT : 0) | (a.flame ? AF_FLAME : 0),
      });
    }
    for (const [id, peer] of s.peers) {
      const st = peer.accepted;
      if (!st || !s.members.has(id)) continue;
      const ps = this.peerSend(id);
      const keyframe = this.tickN - ps.arrowKey >= KEYFRAME;
      if (keyframe) ps.arrowKey = this.tickN;
      const arrows: ArrowState[] = [];
      const seen = new Set<number>();
      for (const a of list) {
        if (Math.abs(a.x - st.x) > VIEW || Math.abs(a.z - st.z) > VIEW) continue;
        seen.add(a.id);
        const sig = arrowSig(a);
        if (!keyframe && ps.arrows.get(a.id) === sig) continue;
        ps.arrows.set(a.id, sig);
        arrows.push(a);
      }
      const removed: number[] = [];
      for (const net of [...ps.arrows.keys()]) {
        if (seen.has(net)) continue;
        ps.arrows.delete(net);
        removed.push(net);
      }
      if (!keyframe && !arrows.length && !removed.length) continue;
      const msg: ArrowsMsg = { tick: this.tickN, keyframe, arrows, removed };
      this.sendTo(id, 'unrel', encodeArrows(msg));
      this.stats.arrows++;
    }
  }

  private hostBlockEntities(): void {
    const s = this.d.session()!;
    for (const [id, peer] of s.peers) {
      const st = peer.accepted;
      if (!st || !s.members.has(id)) continue;
      const ps = this.peerSend(id);
      if (this.tickN - ps.beTick < BE_TICKS) continue;
      ps.beTick = this.tickN;
      const keyframe = this.tickN - ps.beKey >= BE_KEYFRAME;
      if (keyframe) ps.beKey = this.tickN;
      const ents: BeEntry[] = [];
      const removed: Array<{ kind: number; x: number; y: number; z: number }> = [];
      const seen = new Set<string>();
      for (const kind of [BE_CHEST, BE_FURNACE]) {
        for (const e of this.d.beNear(kind, st.x, st.y, st.z, BE_RANGE)) {
          const k = beKey(kind, e.x, e.y, e.z);
          seen.add(k);
          const sig = beSig(e.flat);
          if (!keyframe && ps.be.get(k) === sig) continue;
          ps.be.set(k, sig);
          ents.push({ kind, x: e.x, y: e.y, z: e.z, flat: e.flat });
        }
      }
      for (const k of [...ps.be.keys()]) {
        if (seen.has(k)) continue;
        ps.be.delete(k);
        const cut = k.indexOf(':');
        const [x, y, z] = k.slice(cut + 1).split(',').map(Number);
        removed.push({ kind: Number(k.slice(0, cut)), x, y, z });
      }
      if (!ents.length && !removed.length) continue;
      const msg: BeMsg = { keyframe, ents, removed };
      this.sendTo(id, 'unrel', encodeBe(msg));
      this.stats.be++;
    }
  }

  /** A remote player walked over a drop: hand it over and take it out. */
  private hostPickups(): void {
    if (this.tickN % 2) return;
    const items = this.d.items();
    const xp = this.d.xp();
    if (!items && !xp) return;
    const players = this.d.players();
    if (!players.length) return;
    const me = this.d.session()!.me.id;
    for (const pl of players) {
      for (const it of items?.items ?? []) {
        if (it.collect > 0 || it.stack.count <= 0) continue;
        if (Math.abs(it.x - pl.x) > ITEM_PICK || Math.abs(it.z - pl.z) > ITEM_PICK) continue;
        if (it.y > pl.y + 2.5 || it.y + 0.25 < pl.y - 0.5) continue;
        const net = this.netIdOf(it);
        const own = pl.id === me;
        this.sendTo(pl.id, 'unrel', encodeTake({
          kind: DK_ITEM, id: net, item: it.stack.id, count: Math.min(255, it.stack.count), dmg: it.stack.dmg, granted: !own,
          ex: stackExtras(it.stack),
        }));
        if (!own) it.stack.count = 0;   // the host picked it up itself already
        this.stats.taken++;
      }
      for (const o of xp ? xpOrbs(xp) : []) {
        if (Math.abs(o.x - pl.x) > ORB_PICK || Math.abs(o.z - pl.z) > ORB_PICK) continue;
        if (o.y > pl.y + 2.2 || o.y + 0.5 < pl.y) continue;
        const own = pl.id === me;
        this.sendTo(pl.id, 'unrel', encodeTake({ kind: DK_ORB, id: o.id, item: o.value, count: 1, dmg: 0, granted: !own }));
        if (!own) removeXp(xp!, o.id);
        this.stats.taken++;
      }
    }
  }

  // --------------------------------------------------------------- client

  /** Tell the host about drops we spawned, and about ones that left. */
  private clientDrops(): void {
    const items = this.d.items();
    if (items) {
      const me = this.d.me();
      const live = new Set<number>();
      for (const it of items.items) {
        if (it.stack.id <= 0 || it.stack.count <= 0) continue;
        live.add(it.id);
        if (this.mirrors.has(it)) continue;          // a mirror of the host's, never ours to send
        if (this.pendingByLocal.has(it.id)) continue;
        if (Math.abs(it.x - me.x) > DROP_REACH || Math.abs(it.z - me.z) > DROP_REACH || Math.abs(it.y - me.y) > 8) continue;
        const req = this.reqSeq++ & 0xffff;
        this.pending.set(req, it.id);
        this.pendingByLocal.set(it.id, req);
        this.sendRel(encodeDrop({
          kind: DK_ITEM, req, item: it.stack.id, count: Math.min(255, it.stack.count), dmg: it.stack.dmg,
          x: it.x, y: it.y, z: it.z, vx: it.vx, vy: it.vy, vz: it.vz, ex: stackExtras(it.stack),
        }));
      }
      // a mirror left the world: picked up, burnt, merged, or dropped by the host
      for (const [localId, net] of [...this.localOf]) {
        if (live.has(localId)) continue;
        this.forgetItem(localId, net);
        const it = items.items.find((x) => x.id === localId);
        if (!it) this.sendRel(encodeTake({ kind: DK_ITEM, id: net, item: 0, count: 0, dmg: 0, granted: false }));
      }
      // our own drop vanished before the host answered (swallowed by a merge)
      for (const [localId, req] of [...this.pendingByLocal]) {
        if (live.has(localId)) continue;
        this.pendingByLocal.delete(localId);
        this.pending.delete(req);
      }
    }
    const xp = this.d.xp();
    if (xp) {
      const live = new Set<number>();
      for (const o of xpOrbs(xp)) {
        const net = this.orbNetOf.get(o.id);
        if (net === undefined) continue;
        live.add(net);
      }
      for (const [localId, net] of [...this.orbLocalOf]) {
        if (live.has(net)) continue;
        this.orbLocalOf.delete(net);
        this.orbNetOf.delete(localId);
        this.sendRel(encodeTake({ kind: DK_ORB, id: net, item: 0, count: 0, dmg: 0, granted: false }));
      }
    }
  }

  private clientArrows(): void {
    for (const a of this.d.arrows()) {
      const net = this.arrowNetOf.get(a.id);
      if (net === undefined || a.removed) continue;
      // arrows fly fast and never simulate on a client: follow the host
      const s = this.arrowState.get(net);
      if (!s) continue;
      a.x = s.x; a.y = s.y; a.z = s.z;
      a.vx = s.vx; a.vy = s.vy; a.vz = s.vz;
      a.stuck = (s.flags & AF_STUCK) !== 0;
    }
  }

  /** Client: did we change a block entity the host knows about? */
  private clientBlockEntities(): void {
    if (this.tickN % BE_POLL) return;
    const s = this.d.session()!;
    const ps = this.peerSend(s.hostId);
    const me = this.d.me();
    if (!this.beBase) {
      this.beBase = new Map();
      for (const kind of [BE_CHEST, BE_FURNACE]) {
        for (const e of this.d.beNear(kind, me.x, me.y, me.z, Infinity)) this.beBase.set(beKey(kind, e.x, e.y, e.z), e.flat.slice());
      }
      return;
    }
    for (const kind of [BE_CHEST, BE_FURNACE]) {
      for (const e of this.d.beNear(kind, me.x, me.y, me.z, BE_RANGE)) {
        const k = beKey(kind, e.x, e.y, e.z);
        // neither sent by the host nor in our save at join: a chest / barrel /
        // furnace filled in this session that the host has no record of yet
        // (it only describes containers it holds contents for) - all of it is news
        const mine = this.beSeen.get(k) ?? this.beBase.get(k) ?? null;
        const cmp = !mine ? 0 : kind === BE_FURNACE ? FURNACE_SLOTS : mine.length;
        let same = !!mine && mine.length === e.flat.length;
        for (let i = 0; same && i < cmp; i++) if (mine![i] !== e.flat[i]) same = false;
        if (same) continue;
        if (!ps.rl.bereq.take(this.clock())) return;
        this.beSeen.set(k, e.flat.slice());
        this.sendRel(encodeBeReq({ kind, x: e.x, y: e.y, z: e.z, flat: e.flat }));
        return;
      }
    }
  }

  // ------------------------------------------------------------- messages

  private onMessage(from: string, _ch: 'rel' | 'unrel', data: Uint8Array): void {
    const kind = data[0];
    if (kind !== K_DROPS && kind !== K_ARROWS && kind !== K_BE && kind !== K_TAKE
      && kind !== K_DROP && kind !== K_BEREQ && kind !== K_TNT && kind !== K_DMG) return;
    const s = this.d.session();
    if (!s) return;
    const sender = this.senderId(from);
    if (!sender || !s.members.has(sender)) return;
    try {
      if (s.isHost) {
        if (kind === K_DROP) this.onDropReq(sender, decodeDrop(data));
        else if (kind === K_TAKE) this.onTakeReq(sender, decodeTake(data));
        else if (kind === K_BEREQ) this.onBeReq(sender, decodeBeReq(data));
        else if (kind === K_TNT) this.onTntReq(sender, decodeTntReq(data));
        return;
      }
      if (sender !== s.hostId) return;
      if (kind === K_DROPS) this.applyDrops(decodeDrops(data));
      else if (kind === K_ARROWS) this.applyArrows(decodeArrows(data));
      else if (kind === K_BE) this.applyBe(decodeBe(data));
      else if (kind === K_TAKE) this.applyTake(decodeTake(data));
      else if (kind === K_DMG) this.applyDamage(decodeDamage(data));
    } catch {
      // malformed or hostile payload: the decoders throw, the packet is dropped
    }
  }

  private onDropReq(from: string, r: ReturnType<typeof decodeDrop>): void {
    const st = this.d.session()!.peers.get(from)?.accepted;
    if (!st) return;
    const ps = this.peerSend(from);
    if (!ps.rl.drops.take(this.clock())
      || Math.abs(r.x - st.x) > DROP_REACH || Math.abs(r.z - st.z) > DROP_REACH || Math.abs(r.y - st.y) > 8) {
      this.stats.denied++;
      return;
    }
    const net = this.nextNet++;
    this.originOf.set(net, r.req);
    const local = this.d.spawnDrop(
      { kind: r.kind, id: net, item: r.item, count: r.count, dmg: r.dmg, x: r.x, y: r.y, z: r.z, ex: r.ex },
      net, r.req, r.vx, r.vy, r.vz,
    );
    if (local !== null) this.netOf.set(local, net);
  }

  private onTakeReq(from: string, t: ReturnType<typeof decodeTake>): void {
    if (!this.peerSend(from).rl.drops.take(this.clock())) { this.stats.denied++; return; }
    this.d.removeDrop(t.kind, t.id);
  }

  private onBeReq(from: string, e: BeEntry): void {
    const st = this.d.session()!.peers.get(from)?.accepted;
    if (!st) return;
    if (!this.peerSend(from).rl.bereq.take(this.clock())) { this.stats.denied++; return; }
    if (![e.x, e.y, e.z].every(Number.isInteger)) return;
    if (!withinReach(st.x, st.y, st.z, e.x, e.y, e.z)) { this.stats.denied++; return; }
    this.d.beWrite(e.kind, e.x, e.y, e.z, e.flat);
  }

  private onTntReq(from: string, r: { x: number; y: number; z: number }): void {
    const st = this.d.session()!.peers.get(from)?.accepted;
    if (!st) return;
    if (!this.peerSend(from).rl.tnt.take(this.clock())) { this.stats.denied++; return; }
    if (![r.x, r.y, r.z].every(Number.isInteger)) return;
    if (Math.hypot(r.x + 0.5 - st.x, r.y + 0.5 - st.y, r.z + 0.5 - st.z) > 6) { this.stats.denied++; return; }
    this.prime(r.x, r.y, r.z);
  }

  /** Host: light the charge once, whatever order the block edit arrived in. */
  private prime(x: number, y: number, z: number): void {
    const cell = `${x},${y},${z}`;
    const now = this.clock();
    if (now - (this.primedCells.get(cell) ?? -1e9) < 500) return;
    this.primedCells.set(cell, now);
    if (this.d.getBlock(x, y, z) === TNT_ID) {
      this.d.primeTnt(x, y, z);
      return;
    }
    // the client's block edit landed first: push the charge ourselves
    const tnt = liveTnt();
    if (!tnt) return;
    const charge: Charge = { x: x + 0.5, y, z: z + 0.5, vx: 0, vy: 0.2, vz: 0, fuse: FUSE_TICKS, onGround: false };
    tnt.charges.push(charge);
  }

  // ---------------------------------------------------------- client apply

  private applyDrops(msg: DropsMsg): void {
    const items = this.d.items();
    const xp = this.d.xp();
    const seen = new Set<number>();
    for (const d of msg.drops) {
      seen.add(d.id);
      if (d.kind === DK_ITEM) {
        const local = this.itemFor(d, items);
        if (local === null) continue;
        const it = items?.items.find((x) => x.id === local);
        if (!it || it.collect > 0) continue;
        const moved = Math.abs(it.x - d.x) + Math.abs(it.y - d.y) + Math.abs(it.z - d.z);
        if (moved > SNAP) continue;   // too far to nudge: wait for the next keyframe
        if (moved > 0.01) { it.x = d.x; it.y = d.y; it.z = d.z; }
        if (it.stack.count !== d.count) it.stack.count = d.count;
        if (it.age < d.age) it.age = d.age;
      } else {
        const local = this.orbFor(d, xp);
        if (local !== null && xp) placeXp(xp, local, d.x, d.y, d.z);
      }
    }
    if (msg.keyframe) {
      for (const net of [...this.localOf.keys()]) if (!seen.has(net)) this.itemGone(net);
      for (const net of [...this.orbLocalOf.keys()]) if (!seen.has(net)) this.orbGone(net);
    }
    for (const net of msg.removed) {
      if (net === 0) continue;
      if (this.localOf.has(net)) this.itemGone(net);
      else if (this.orbLocalOf.has(net)) this.orbGone(net);
    }
  }

  private itemGone(net: number): void {
    const local = this.localOf.get(net);
    this.localOf.delete(net);
    if (local === undefined) return;
    this.netOf.delete(local);
    const it = this.d.items()?.items.find((x) => x.id === local);
    if (it) it.stack.count = 0;   // also while it plays the pickup animation
  }

  /** The mirror of `d.id`, or the local drop this state answers (origin match). */
  private itemFor(d: DropState, items: ItemEntityManager | null): number | null {
    if (d.kind !== DK_ITEM) return null;
    if (!items) return null;
    const known = this.localOf.get(d.id);
    if (known !== undefined && items.items.some((x) => x.id === known)) return known;
    this.localOf.delete(d.id);
    if (d.origin) {
      const local = this.pending.get(d.origin);
      if (local !== undefined) {
        const it = items.items.find((x) => x.id === local);
        if (it) {
          this.pending.delete(d.origin);
          this.pendingByLocal.delete(local);
          this.localOf.set(d.id, local);
          this.netOf.set(local, d.id);
          this.mirrors.add(it);   // the host owns it now: stop offering it
          return local;
        }
      }
    }
    const e = items.spawn(stackWith(d.item, d.count, d.dmg, d.ex), d.x, d.y, d.z, 0, 0, 0, 0);
    if (!e) return null;
    e.age = d.age;
    this.mirrors.add(e);
    this.localOf.set(d.id, e.id);
    this.netOf.set(e.id, d.id);
    return e.id;
  }

  private orbFor(d: DropState, xp: XpOrbManager | null): number | null {
    if (!xp || d.kind !== DK_ORB) return null;
    const have = this.orbLocalOf.get(d.id);
    if (have !== undefined) return have;
    const before = new Set(xpOrbs(xp).map((o) => o.id));
    xp.spawn(d.x, d.y + 0.25, d.z, d.item);   // d.item is one vanilla-sized orb
    for (const o of xpOrbs(xp)) {
      if (before.has(o.id)) continue;
      this.orbLocalOf.set(d.id, o.id);
      this.orbNetOf.set(o.id, d.id);
      return o.id;
    }
    return null;
  }

  private orbGone(net: number): void {
    const local = this.orbLocalOf.get(net);
    this.orbLocalOf.delete(net);
    if (local === undefined) return;
    this.orbNetOf.delete(local);
    const xp = this.d.xp();
    if (xp) removeXp(xp, local);
  }

  private applyArrows(msg: ArrowsMsg): void {
    const seen = new Set<number>();
    for (const a of msg.arrows) {
      seen.add(a.id);
      this.arrowState.set(a.id, a);
      if (this.arrowLocalOf.has(a.id)) continue;
      const made = this.d.spawnArrow(a);
      if (made === null) continue;
      this.arrowLocalOf.set(a.id, made);
      this.arrowNetOf.set(made, a.id);
    }
    for (const net of msg.removed) this.arrowGone(net);
    if (msg.keyframe) {
      for (const net of [...this.arrowLocalOf.keys()]) if (!seen.has(net)) this.arrowGone(net);
    }
  }

  private arrowGone(net: number): void {
    this.arrowState.delete(net);
    const local = this.arrowLocalOf.get(net);
    this.arrowLocalOf.delete(net);
    if (local !== undefined) {
      this.arrowNetOf.delete(local);
      this.d.removeArrow(local);
    }
  }

  private applyBe(msg: BeMsg): void {
    for (const e of msg.ents) {
      if (!e.flat.length) continue;
      this.d.beWrite(e.kind, e.x, e.y, e.z, e.flat);
      this.beSeen.set(beKey(e.kind, e.x, e.y, e.z), e.flat.slice());
    }
    for (const e of msg.removed) {
      this.d.beDrop(e.kind, e.x, e.y, e.z);
      this.beSeen.delete(beKey(e.kind, e.x, e.y, e.z));
    }
  }

  /**
   * The host took a drop out of the world (we picked it up, or it saw us next
   * to it). Credit it here exactly once: our own item manager already did the
   * job if the pickup animation is running, and a mirror we never had is
   * nothing to credit.
   */
  private applyTake(t: ReturnType<typeof decodeTake>): void {
    if (t.kind === DK_ITEM) {
      const local = this.localOf.get(t.id);
      if (local === undefined) return;
      const it = this.d.items()?.items.find((x) => x.id === local);
      this.forgetItem(local, t.id);
      if (!it || it.collect > 0) return;   // already in our hands
      it.stack.count = 0;
      if (t.granted && t.count > 0) this.d.giveItem(t.item, t.count, t.dmg, t.ex);
      return;
    }
    if (this.orbLocalOf.has(t.id)) return;   // our own orb manager collects it
    const xp = this.d.xp();
    if (!xp || !t.granted) return;
    const me = this.d.me();
    xp.spawn(me.x, me.y + 0.5, me.z, t.item);   // collected for us: credit it locally
  }

  private applyDamage(d: ReturnType<typeof decodeDamage>): void {
    if (d.amount <= 0) return;
    this.d.localHit(d);
    this.stats.dmg++;
  }

  /** Host: a mob or a blast hurt `id`. The client applies it to its own
   *  player, which owns its health (PSTATE carries it back to us). */
  hurtPeer(id: string, hit: NetHit): void {
    const s = this.d.session();
    if (!s || !s.isHost || !s.members.has(id)) return;
    this.sendTo(id, 'unrel', encodeDamage(hit));
    this.stats.dmg++;
  }

  /** The net id a host-side drop carries (0 when it has none yet). */
  netIdFor(it: ItemEntity): number { return this.netOf.get(it.id) ?? 0 }

  // ----------------------------------------------------------------- utils

  private netIdOf(it: ItemEntity): number {
    let net = this.netOf.get(it.id);
    if (net === undefined) {
      net = this.nextNet++;
      this.netOf.set(it.id, net);
    }
    return net;
  }

  private forgetItem(localId: number, net: number): void {
    if ((globalThis as { __dbgDrop?: number }).__dbgDrop !== undefined) {
      const g = globalThis as { __dbg?: unknown[]; __dbgDrop?: number };
      (g.__dbg ??= []).push(`forget local=${localId} net=${net}`);
      if (g.__dbg.length > (g.__dbgDrop as number)) g.__dbgDrop = g.__dbg.length + 20;
    }
    this.localOf.delete(net);
    this.netOf.delete(localId);
  }

  private peerSend(id: string): PeerSend {
    let p = this.peers.get(id);
    if (!p) {
      p = {
        drops: new Map(), dropKey: 0, arrows: new Map(), arrowKey: 0, be: new Map(), beKey: 0, beTick: 0,
        rl: { drops: new RateLimiter(20, 40), bereq: new RateLimiter(4, 8), tnt: new RateLimiter(2, 4) },
      };
      this.peers.set(id, p);
    }
    return p;
  }

  private senderId(tid: string): string | null {
    const s = this.d.session();
    if (!s) return null;
    for (const p of s.peers.values()) if (p.tid === tid) return p.id;
    return null;
  }

  private tidOf(id: string): string | null {
    const s = this.d.session();
    if (!s) return null;
    for (const p of s.peers.values()) if (p.id === id) return p.tid;
    return null;
  }

  private clock(): number { return this.d.session()?.now() ?? 0 }

  private sendTo(id: string, ch: 'rel' | 'unrel', data: Uint8Array): void {
    const tid = this.tidOf(id);
    const s = this.d.session();
    if (tid && s) s.transport.send(tid, ch, data);
  }

  private sendRel(data: Uint8Array): void {
    const s = this.d.session();
    if (!s || !s.hostId) return;
    const tid = this.tidOf(s.hostId);
    if (tid) s.transport.send(tid, 'rel', data);
  }
}

function dropSig(d: DropState): number {
  let h = d.id * 31 + d.kind;
  h = (h * 31 + d.item) | 0;
  h = (h * 31 + d.count) | 0;
  h = (h * 31 + d.dmg) | 0;
  if (d.ex) for (const v of d.ex) h = (h * 31 + v) | 0;
  h = (h * 31 + Math.round(d.x * 32)) | 0;
  h = (h * 31 + Math.round(d.y * 32)) | 0;
  h = (h * 31 + Math.round(d.z * 32)) | 0;
  h = (h * 31 + (d.age & 0xff)) | 0;
  return h;
}

function arrowSig(a: ArrowState): number {
  let h = a.id * 31 + a.flags;
  h = (h * 31 + Math.round(a.x * 32)) | 0;
  h = (h * 31 + Math.round(a.y * 32)) | 0;
  h = (h * 31 + Math.round(a.z * 32)) | 0;
  h = (h * 31 + Math.round(a.vx * 32)) | 0;
  h = (h * 31 + Math.round(a.vy * 32)) | 0;
  h = (h * 31 + Math.round(a.vz * 32)) | 0;
  return h;
}

function beKey(kind: number, x: number, y: number, z: number): string { return `${kind}:${x},${y},${z}` }

function beSig(flat: number[]): number {
  let h = 0x811c9dc5 | 0;
  for (let i = 0; i < flat.length; i++) {
    h = (h ^ flat[i]) | 0;
    h = Math.imul(h, 0x01000193) | 0;
  }
  return h;
}
